/**
 * Avatar člověka — jedno místo pro kolečko s fotkou nebo iniciálami.
 *
 * Barva iniciál se počítá z jména, takže je pro téhož člověka pokaždé stejná
 * a seznam jde přelétnout očima. Paleta i výpočet žily ve dvou kopiích
 * (`ChatWindow`, `Zpravy`) a online tečka se kreslila ručně na třech místech —
 * pokaždé s vlastní velikostí a rámečkem.
 */

const BARVY = [
    '#6C4192', '#0CC0DF', '#2563eb', '#16a34a', '#dc2626',
    '#9333ea', '#0891b2', '#ca8a04', '#c2410c', '#4f46e5',
];

/** Barva podle jména — stabilní, ne náhodná. */
export function getAvatarBarva(jmeno) {
    if (!jmeno) return '#C97000';
    let hash = 0;
    for (let i = 0; i < jmeno.length; i++) hash = jmeno.charCodeAt(i) + ((hash << 5) - hash);
    return BARVY[Math.abs(hash) % BARVY.length];
}

/** Iniciály — nejvýš dvě, z prvních dvou slov. */
export function iniciály(jmeno) {
    if (!jmeno) return '?';
    return jmeno.split(' ').filter(Boolean).slice(0, 2)
        .map((s) => s[0]?.toUpperCase())
        .join('');
}

/**
 * @param {string} [props.url] - fotka; bez ní se kreslí iniciály
 * @param {string} props.jmeno
 * @param {number} [props.size=40]
 * @param {boolean} [props.online] - zelená tečka v pravém dolním rohu
 * @param {boolean} [props.isDark] - barva rámečku tečky (splývá s podkladem)
 * @param {string} [props.ramecekTecky] - podklad, na kterém avatar leží; bez
 *        něj se bere plocha stránky podle motivu
 * @param {() => void} [props.onChybaFoto] - fotka se nenačetla (404, smazaný
 *        soubor); volající si to zapamatuje a příště pošle `url` prázdné,
 *        aby místo rozbité ikony byly iniciály
 */
export default function Avatar({ url, jmeno, size = 40, online = false, isDark = false, ramecekTecky, onChybaFoto }) {
    const tecka = Math.max(9, Math.round(size * 0.3));

    return (
        <div className="relative shrink-0" style={{ width: size, height: size }}>
            {url ? (
                <img
                    src={url}
                    alt=""
                    onError={onChybaFoto}
                    className="rounded-full object-cover"
                    style={{ width: size, height: size, display: 'block' }}
                />
            ) : (
                <div
                    className="rounded-full flex items-center justify-center text-white font-bold"
                    style={{
                        width: size, height: size,
                        background: getAvatarBarva(jmeno),
                        fontSize: Math.round(size * 0.35),
                    }}
                >
                    {iniciály(jmeno)}
                </div>
            )}
            {online && (
                <span
                    aria-hidden="true"
                    className="absolute rounded-full"
                    style={{
                        right: 0, bottom: 0,
                        width: tecka, height: tecka,
                        background: '#10B981',
                        border: `2px solid ${ramecekTecky || (isDark ? '#141416' : '#fff')}`,
                    }}
                />
            )}
        </div>
    );
}
